import {  useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { HclSnippet } from '@/shared/components/HclSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import {   CLI_INSTALL_GITHUB, CLI_INSTALL_GITLAB, CLI_GITLAB_SETUP, CLI_GITLAB_ID_TOKENS } from '@/features/integrations/snippets'

// ── Pipeline Trust (OIDC) ─────────────────────────────────────────────────────
export function OidcTrustGuide({ org, onSwitchGuide }: { org: OrgItem | null; onSwitchGuide: (id: GuideId) => void }) {
  const navigate = useNavigate()
  const [platform, setPlatform]       = useState<Platform>('github')
  const [pubPlatform, setPubPlatform] = useState<Platform>('github')
  const orgSlug = org?.slug ?? '<org-slug>'
  const wsName  = '<workspace>'

  // The CI tabs let the CLI do the OIDC handshake; `curl` keeps the raw-JWT recipe.
  const ciPlatforms: Platform[] = ['github', 'gitlab', 'curl']

  const backendHcl = `terraform {
  backend "http" {
    address        = "https://registry.terramantle.dev/state/${orgSlug}/${wsName}"
    lock_address   = "https://registry.terramantle.dev/state/${orgSlug}/${wsName}.lock"
    unlock_address = "https://registry.terramantle.dev/state/${orgSlug}/${wsName}.lock"
    username       = "x-oidc"
    # export TF_HTTP_PASSWORD = <oidc jwt - fetched in CI, no stored secret>
  }
}`

  const githubSnippet = `name: terraform-oidc

on:
  push:
    branches: [main]

permissions:
  id-token: write   # required - allows the job to request an OIDC token
  contents: read

jobs:
  apply:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}
    steps:
      - uses: actions/checkout@v4
      - uses: opentofu/setup-opentofu@v1

${CLI_INSTALL_GITHUB}

      # No token step: terramantle sees GITHUB_ACTIONS and fetches the OIDC
      # token itself, then exports TF_HTTP_PASSWORD from it.
      - name: Terraform init & apply
        run: |
          eval "$(terramantle auth env --terraform)"
          tofu init
          tofu apply -auto-approve`

  const gitlabSnippet = `stages:
  - deploy

apply:
  stage: deploy
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}
${CLI_GITLAB_SETUP}
  # terramantle sees GITLAB_CI and uses $TERRAMANTLE_ID_TOKEN as the bearer.
  script:
    - eval "$(terramantle auth env --terraform)"
    - tofu init
    - tofu apply -auto-approve
  only:
    - main`

  const curlSnippet = `# GitHub Actions - request the runner's OIDC token by hand
TOKEN=$(curl -s \\
  -H "Authorization: bearer $ACTIONS_ID_TOKEN_REQUEST_TOKEN" \\
  "$ACTIONS_ID_TOKEN_REQUEST_URL&audience=api://AzureADTokenExchange" \\
  | jq -r .value)
export TF_HTTP_PASSWORD=$TOKEN

# GitLab CI - the id_tokens block already put the JWT in the environment
# export TF_HTTP_PASSWORD=$TM_JWT

tofu init
tofu apply -auto-approve`

  const githubPublishSnippet = `name: publish-module-oidc

on:
  push:
    branches: [main]

permissions:
  id-token: write   # required for OIDC
  contents: write   # repo tag pushes the release tags

jobs:
  publish:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0

${CLI_INSTALL_GITHUB}

      - name: Publish and tag
        run: |
          terramantle modules publish --provider aws --ci
          terramantle repo tag`

  const gitlabPublishSnippet = `publish-modules:
  stage: deploy
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}
    GIT_DEPTH: 0
  script:
    - apt-get update && apt-get install -y curl git
${CLI_INSTALL_GITLAB}
    - terramantle modules publish --provider aws --ci
    - terramantle repo tag
  only:
    - main`

  const curlPublishSnippet = `# Raw PUT with the runner's OIDC JWT as the credential
VERSION="\${CI_COMMIT_TAG#v}"
tar -czf module.tar.gz -C . .
curl -X PUT \\
  "https://registry.terramantle.dev/v1/modules/${orgSlug}/my-module/aws/$VERSION" \\
  -u "terramantle-bot:$TM_JWT" \\
  -H "Content-Type: application/octet-stream" \\
  --data-binary @module.tar.gz`

  const subjectFormats = `# GitHub - exact repo:
repo:owner/repo-name

# GitHub - all repos in a GitHub org:
repo:your-github-org/*

# GitLab - exact project:
project_path:group/project

# GitLab - all projects in a group:
project_path:group/*`

  return (
    <div>
      <h3 style={s.guideTitle}>Pipeline Trust</h3>
      <p style={s.para}>
        Let GitHub Actions and GitLab CI authenticate directly using their native runner OIDC tokens -
        no stored client secrets required. Terramantle verifies the JWT against the provider's
        JWKS and grants write access when the repo subject claim matches the trusted repo reference in your Organisation settings → <button style={s.linkBtn} onClick={() => navigate(org ? `/org/${org.slug}/settings?tab=vcs-trust` : '/')}>Pipeline Trust</button> section.
      </p>
      <p style={s.para}>
        The <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-getting-started')}>Terramantle CLI</button>{' '}
        does this handshake for you - it detects the runner, collects the OIDC token and exports the credential,
        so the pipelines below have no token step at all.
      </p>

      <h3 style={s.subTitle}>How it works</h3>
      <ol style={s.list}>
        <li>The pipeline runner has a signed JWT from GitHub / GitLab automatically.</li>
        <li>JWT is passed as <code style={s.code}>TF_HTTP_PASSWORD</code> (or as the CLI's bearer).</li>
        <li>Terramantle verifies the signature against the provider JWT signing keys (JWKS).</li>
        <li>The <code style={s.code}>sub</code> claim is matched against trusted subjects in your org.</li>
        <li>On match, write access is granted - no token generation or storage needed.</li>
      </ol>

      <h3 style={s.subTitle}>1. Claim a trusted repo</h3>
      <p style={s.para}>
        Add a trusted subject in{' '}
        Organisation settings → <button style={s.linkBtn} onClick={() => navigate(org ? `/org/${org.slug}/settings?tab=vcs-trust` : '/')}>Pipeline Trust</button> tab.
      </p>
      <ShellSnippet code={subjectFormats} language="bash" />
      <p style={s.para}>
        Each entry also carries <strong>allowed refs</strong> - the branches (and <code style={s.code}>tag:</code> globs) the
        pipeline may <em>push</em> from, defaulting to <code style={s.code}>main</code>. A state push, lock-file upload or
        module publish from any other ref is refused with <code style={s.code}>403 ci_ref_not_allowed</code>. Reads, locks
        and plan pushes are not restricted, so a pull-request pipeline still plans with locking and the plan gate still
        sees it. The subject itself cannot carry a <code style={s.code}>:ref:</code> suffix; put the branch in allowed refs.
      </p>

      <h3 style={s.subTitle}>2. Backend config</h3>
      <p style={s.para}>
        Only needed when you drive <code style={s.code}>tofu</code> yourself with the raw JWT - the CLI's{' '}
        <code style={s.code}>auth env --terraform</code> sets <code style={s.code}>TF_HTTP_PASSWORD</code> either way.
      </p>
      <HclSnippet code={backendHcl} copyText={backendHcl} />

      <h3 style={s.subTitle}>3. CI pipeline</h3>
      <PlatformTabs value={platform} onChange={setPlatform} only={ciPlatforms} />
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}
      {platform === 'curl'   && <ShellSnippet code={curlSnippet}   language="bash" />}

      <h3 style={s.subTitle}>4. Module publishing via OIDC</h3>
      <p style={s.para}>
        The same OIDC identity authenticates module publishes - no bot token needed. Run it on the default
        branch and the CLI versions, uploads and tags each changed module.
      </p>
      <PlatformTabs value={pubPlatform} onChange={setPubPlatform} only={ciPlatforms} />
      {pubPlatform === 'github' && <ShellSnippet code={githubPublishSnippet} language="yaml" />}
      {pubPlatform === 'gitlab' && <ShellSnippet code={gitlabPublishSnippet} language="yaml" />}
      {pubPlatform === 'curl'   && <ShellSnippet code={curlPublishSnippet}   language="bash" />}

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li>OIDC tokens get write access on their org; the only restriction is <em>which refs may push</em>, set per trust entry. Reads, locks and plans are never ref-restricted.</li>
        <li>Org <strong>Access Rules</strong> (country / ASN / IP) apply to OIDC tokens as machine callers; a refusal is <code style={s.code}>403 access_rule_denied</code>.</li>
        <li><code style={s.code}>TERRAMANTLE_ORG</code> is required in CI: an OIDC token is server-resolved from repo trust, so there is no org lookup for the CLI to use.</li>
        <li>GitLab CI is supported for <code style={s.code}>gitlab.com</code> hosted projects.</li>
        <li>JWKS are cached for 1 hour - key rotations propagate within that window.</li>
        <li>OIDC bots cannot perform admin actions (manage members, settings).</li>
      </ul>
    </div>
  )
}
